import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Menu, MenuItem } from '@material-ui/core';
import ItemSourceMeanValidatorItemsModel from 'models/ItemSourceMeanValidatorItemsModel';
import { ItemSourceMeanValidatorModel } from 'models/ItemSourceMeanValidatorModel';
import { IItemSourceMeanValidatorItem } from 'types/itemSourceMeanValidator';
import { ItemSourceMeanValidatorItemStatus as ItemStatus } from 'constants/itemSourceMeanValidatorStatus';

interface Props {
  itemsModel: ItemSourceMeanValidatorItemsModel;
  model: ItemSourceMeanValidatorModel;
  onActionComplete: () => void;
}

const ItemSourceMeanValidatorBulkActions: React.FC<Props> = ({ itemsModel, model, onActionComplete }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [busy, setBusy] = useState(false);

  const { countKeep, countChange, countReview } = useMemo(() => {
    if (!itemsModel.gridData || !Array.isArray(itemsModel.gridData))
      return { countKeep: 0, countChange: 0, countReview: 0 };
    const items = itemsModel.gridData as IItemSourceMeanValidatorItem[];
    let keep = 0,
      change = 0,
      review = 0;
    items.forEach((it) => {
      if (it.status === ItemStatus.NEEDS_USER_REVIEW) {
        review++;
        // Count based on AI decision
        if (it.ai_result?.decision === 'keep') keep++;
        else if (it.ai_result?.decision === 'change') change++;
      }
    });
    return { countKeep: keep, countChange: change, countReview: review };
  }, [itemsModel.gridData]);

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => setAnchor(e.currentTarget);
  const closeMenu = () => setAnchor(null);

  const doBulk = async (action: 'keep' | 'change') => {
    closeMenu();
    if (busy) return;
    setBusy(true);
    try {
      if (action === 'keep') await itemsModel.bulkKeep();
      else if (action === 'change') await itemsModel.bulkChange();
      await model.reload();
      onActionComplete();
    } catch (e) {
      console.error('Bulk action failed', e);
    } finally {
      setBusy(false);
    }
  };

  if (countReview === 0) return null;

  return (
    <>
      <BulkActionsButton onClick={openMenu} disabled={busy || itemsModel.loadingGrid}>
        {busy ? 'Processing...' : 'Bulk Actions'}
      </BulkActionsButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={closeMenu}>
        <MenuItem onClick={() => doBulk('keep')} disabled={countKeep === 0}>
          Keep All ({countKeep})
        </MenuItem>
        <MenuItem onClick={() => doBulk('change')} disabled={countChange === 0}>
          Apply All Changes ({countChange})
        </MenuItem>
      </Menu>
    </>
  );
};

export default ItemSourceMeanValidatorBulkActions;

const BulkActionsButton = styled.button`
  padding: 8px 16px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s;
  &:hover:not(:disabled) {
    background-color: #45a049;
  }
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;
